<template>
    <div>
        <b-modal ref="modal" title="Merge Task Answers into Document" @hidden="resetModal" @ok.prevent="performMerge"
            :ok-disabled="!selectedDocument || processing" ok-title="Merge & Generate PDF" centered no-fade>
        <div>
            <b-form-group>
                <SearchComponent v-model="selectedDocument" 
                    label="Select Document Template" 
                    search-u-r-l="/api/TaskCompletionAnswersMergeDocument"
                    :prepopulate="true"
                    :params="{ activeOnly: true }"
                    :option-label="getDocumentLabel"
                    :clearable="false" />
            </b-form-group>

            <b-form-group class="mt-4">
                <b-form-checkbox v-model="linkToPatientDocuments">
                    Automatically link generated PDF to patient documents
                    <HelpPopup>
                        When enabled, the generated PDF will be added to the patient's document collection
                        (if the task is associated with a patient).
                    </HelpPopup>
                </b-form-checkbox>
            </b-form-group>

        </div>
        <template #modal-footer="{ ok, cancel }">
            <b-button @click="cancel">Cancel</b-button>
            <b-button variant="primary" @click="ok" :disabled="!selectedDocument || processing">
                <b-spinner v-if="processing" small class="mr-1" />
                <i v-else class="fa fa-file-pdf-o mr-1" />
                {{ processing ? 'Processing...' : 'Merge & Generate PDF' }}
            </b-button>
        </template>
        </b-modal>

        <!-- Loading Modal -->
        <b-modal ref="loadingModal" id="pdf-generation-modal" title="Generating PDF" 
            centered hide-footer no-close-on-backdrop no-close-on-esc hide-header-close no-fade>
            <div class="text-center py-3">
                <b-spinner class="mb-3" label="Generating PDF"></b-spinner>
                <p>Please wait while we generate your PDF document...</p>
            </div>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Ref } from "vue-property-decorator";
import { TaskCompletionAnswersMergeDocument } from "@/models/TaskCompletionAnswersMergeDocument";
import SearchComponent from "@/components/SearchComponent.vue";
import HelpPopup from "@/components/HelpPopup.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    components: { SearchComponent, HelpPopup }
})
export default class MergeDocumentSelectionModal extends Vue {
    @Ref('modal') modal!: any;
    @Ref('loadingModal') loadingModal!: any;

    private taskId: number = 0;
    private selectedDocument: TaskCompletionAnswersMergeDocument | null = null;
    private linkToPatientDocuments: boolean = true;
    private processing: boolean = false;
    
    // Store values for download after modal closes
    private downloadTaskId: number = 0;
    private downloadDocumentName: string = '';

    getDocumentLabel(document: TaskCompletionAnswersMergeDocument): string {
        if (!document) return '';
        return document.name;
    }

    show(taskId: number) {
        this.taskId = taskId;
        this.modal.show();
    }

    hide() {
        this.modal.hide();
    }

    resetModal() {
        this.taskId = 0;
        this.selectedDocument = null;
        this.linkToPatientDocuments = true;
        this.processing = false;
        this.downloadTaskId = 0;
        this.downloadDocumentName = '';
    }

    async performMerge() {
        if (!this.selectedDocument || !this.taskId) {
            return;
        }

        this.processing = true;
        
        this.loadingModal.show();

        const mergeDocumentId = this.selectedDocument.id;

        try {
            const response = await this.$http.post(`/api/Task/merge-answers/${this.taskId}`, {
                mergeDocumentId,
                linkToPatientDocuments: this.linkToPatientDocuments
            });

            const result = response.data;
            
            this.loadingModal.hide();
            
            if (result.success && result.imageId) {
                this.$notification(NotificationOptions.success("Task answers have been successfully merged into a PDF document."));

                // Store values for download before modal is hidden/reset
                this.downloadTaskId = this.taskId;
                this.downloadDocumentName = this.selectedDocument?.name || 'Merged';

                this.$bvModal.msgBoxConfirm(
                    "Would you like to download the generated PDF now?",
                    {
                        title: "Download Merged PDF",
                        size: 'sm',
                        buttonSize: 'sm',
                        okVariant: 'primary',
                        okTitle: 'Download',
                        cancelTitle: 'Close',
                        centered: true,
                        noFade: true
                    }
                ).then(async (download) => {
                    if (download) {
                        await this.downloadPdf(result.imageId);
                    }
                    
                    // Only hide and emit after user has responded to download dialog
                    this.$emit('merge-completed', {
                        taskId: this.downloadTaskId,
                        documentId: mergeDocumentId,
                        imageId: result.imageId,
                        linkedToPatient: this.linkToPatientDocuments
                    });

                    this.hide();
                });
            } else {
                throw new Error("Merge operation did not complete successfully");
            }
        } catch (error: any) {
            // Hide loading modal
            this.loadingModal.hide();
            
            console.error("Error merging task answers:", error);

            let errorMessage = "An error occurred while merging the task answers.";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data) {
                errorMessage = error.response.data;
            }

            this.$notification(NotificationOptions.error(errorMessage));
        } finally {
            this.processing = false;
        }
    }

    async downloadPdf(imageId: number) {
        try {
            const response = await this.$http.get(`/api/Image/${imageId}`, {
                responseType: 'blob'
            });

            const filename = `Task_${this.downloadTaskId}_${this.downloadDocumentName}_Answers.pdf`;

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading PDF:", error);
            this.$notification(NotificationOptions.error("Error downloading the generated PDF"));
        }
    }
}
</script>